import type { Ref } from 'vue'
import type { INews } from '~/components/entity/news/types'
import type { IResponseData } from '~/types'
import { toOrigin } from '~/lib/origin'

const DRAFT_MESSAGE = 'banks:news-preview:draft'
const READY_MESSAGE = 'banks:news-preview:ready'

type TNewsDraft = Pick<
  INews,
  'title' | 'summary' | 'content' | 'largeImage' | 'largeImageCopyright' | 'carousel'
>

/**
 * Applies the unsaved draft sent by the Nova news editor to the previewed news item.
 * Works only on the client, inside a frame, and only for messages that come from the
 * parent window on the configured Nova origin.
 */
export function useNewsPreviewDraft(
  data: Ref<IResponseData<INews> | null | undefined>,
  enabled: boolean
): void {
  if (!enabled || !import.meta.client) return

  const novaOrigin = toOrigin(useRuntimeConfig().public.novaOrigin)
  if (!novaOrigin) return

  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== novaOrigin || event.source !== window.parent) return
    if (event.data?.type !== DRAFT_MESSAGE || !data.value?.data) return

    const draft = (event.data.news ?? {}) as Partial<Record<keyof TNewsDraft, unknown>>
    const current = data.value.data
    const text = (value: unknown, fallback: string): string =>
      typeof value === 'string' ? value : fallback

    data.value = {
      ...data.value,
      data: {
        ...current,
        title: text(draft.title, current.title),
        summary: text(draft.summary, current.summary),
        content: text(draft.content, current.content),
        largeImage:
          typeof draft.largeImage === 'string' || draft.largeImage === null
            ? draft.largeImage || undefined
            : current.largeImage,
        largeImageCopyright: text(draft.largeImageCopyright, current.largeImageCopyright),
        carousel: Array.isArray(draft.carousel)
          ? (draft.carousel as INews['carousel'])
          : current.carousel
      }
    }
  }

  onMounted(() => {
    window.addEventListener('message', onMessage)

    if (window.parent !== window) {
      window.parent.postMessage({ type: READY_MESSAGE }, novaOrigin)
    }
  })

  onBeforeUnmount(() => window.removeEventListener('message', onMessage))
}
